import { MainMenu, SubMenuType, Widget } from 'rangeConfig/@types';
import ProductSwiper from 'rangeConfig/shared/widgets/ProductSwiper';
import FrontLayouts from 'rangeConfig/besta/widgets/fronts/FrontLayouts';
import checkWidget from 'rangeConfig/shared/widgets/checkWidget';
import showDoors from 'rangeConfig/shared/behaviors/showDoors';
import disableUndoRedo from 'rangeConfig/shared/behaviors/disableUndoRedo';
import enableUndoRedo from 'rangeConfig/shared/behaviors/enableUndoRedo';
import platform from 'rangeConfig/shared/utils/platform';
import { colorFilter } from 'rangeConfig/shared/filters/configFilters';
import { frontTypeFilter } from 'rangeConfig/besta/filters/configFilters';
import {
  combinationsProductListConfig,
  doorsAndDrawersProductListConfig
} from './productListConfig';
import withProducts from 'rangeConfig/shared/widgets/withProducts';
import ProductList from 'rangeConfig/shared/widgets/ProductList';
import forgetFrontChangeUserChoices from 'rangeConfig/shared/behaviors/forgetFrontChangeUserChoices';
import withMessage from 'rangeConfig/shared/widgets/withMessage';
import ProductSortFilterWidget from 'rangeConfig/shared/widgets/ProductSortFilterWidget';
import { isKiosk } from 'utils/platform';
import CategoryAdditionalInlineMessage from 'components/ProductSidebar/CategoryWidget/CategoryAdditionalInlineMessage';
import withInlineMessage from 'rangeConfig/shared/widgets/withInlineMessage';
import { space, SpaceProps } from 'styled-system';
import styled from 'styled-components';
import withErrorMessage from 'rangeConfig/shared/widgets/withErrorMessage';
import {
  getAddingBestaEketButNoMatchingFramesErrorMessage,
  getAddingMixedBestaEketFramesErrorMessage,
  getNoFramesInSceneErrorMessage
} from 'rangeConfig/shared/utils/errorMessages';
import ErrorMessageInline from 'rangeConfig/shared/widgets/ErrorMessageInline';
import { CoreApi } from '@inter-ikea-storageone/range';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import {
  hideMixedBestaFramesInSceneErrorMessage,
  hideNoBestaFramesInSceneErrorMessage
} from 'rangeConfig/besta/rules/hideMenuRules';
import {
  sortByName,
  sortByPriceAscending,
  sortByPriceDescending
} from 'rangeConfig/shared/sorting/sortingConfigs';

const Divider = styled.div<SpaceProps>`
  ${space}
  display: block;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.grey[200]};
  margin-top: ${({ theme }) => theme.space.l};
`;

const enableFrontListTab = isKiosk();

const widgets = {
  desktop: [
    {
      id: 'error-message-no-frames-in-scene',
      component: withErrorMessage(getNoFramesInSceneErrorMessage)(ErrorMessageInline),
      hidden: (coreApi: CoreApi) => hideNoFramesInSceneErrorMessage(coreApi)
    },
    {
      id: 'error-message-no-besta-frames-in-scene',
      component: withErrorMessage(getAddingBestaEketButNoMatchingFramesErrorMessage)(
        ErrorMessageInline
      ),
      hidden: (coreApi: CoreApi) => hideNoBestaFramesInSceneErrorMessage(coreApi)
    },
    {
      id: 'error-message-mixed-besta-frames-in-scene',
      component: withErrorMessage(getAddingMixedBestaEketFramesErrorMessage)(ErrorMessageInline),
      hidden: (coreApi: CoreApi) => hideMixedBestaFramesInSceneErrorMessage(coreApi)
    }
  ],
  mobile: [
    {
      id: 'fronts-style-product-swiper',
      component: withProducts(ProductSwiper, combinationsProductListConfig),
      title: 'storageone.frontStyleTabTitle'
    },
    {
      id: 'fronts-layout-layout',
      component: checkWidget(FrontLayouts),
      title: 'storageone.frontLayoutTabTitle'
    }
  ]
};

const desktopTabs = () => {
  const styleTab = {
    id: 'front-style-tab',
    title: 'storageone.frontStyleTabSelectedMenuTitle',
    sorting: [sortByName, sortByPriceAscending, sortByPriceDescending],
    widgets: [
      {
        id: 'fronts-sort-filter-widget',
        component: ProductSortFilterWidget
      },
      {
        id: 'front-style-list',
        component: withProducts(ProductList, combinationsProductListConfig)
      }
    ]
  };
  const combinationsTab = {
    id: 'front-combinations-tab',
    title: 'storageone.combinationsTabTitle',
    behaviors: {
      subMenuEntered: [disableUndoRedo],
      subMenuExited: [enableUndoRedo]
    },
    widgets: [
      {
        id: 'front-combinations-list',
        component: checkWidget(FrontLayouts)
      }
    ]
  };

  const frontListTab = {
    id: 'front-list-tab',
    title: 'storageone.doorAndDrawerListTabTitle',
    widgets: [
      {
        id: 'front-list-lis-message',
        component: withMessage('storageone.doorAndDrawerListTabBody')
      },
      {
        id: 'frontListProductFilter',
        component: () => ProductSortFilterWidget()
      } as Widget,
      {
        id: 'frontListDivider',
        component: Divider
      },
      {
        id: 'frontListAdditionalMessage',
        component: withInlineMessage(
          'storageone.additionalDoorDrawerInfo',
          true
        )(CategoryAdditionalInlineMessage)
      },
      {
        id: 'front-list-list',
        component: withProducts(ProductList, doorsAndDrawersProductListConfig)
      }
    ]
  };
  return enableFrontListTab
    ? [styleTab, combinationsTab, frontListTab]
    : [styleTab, combinationsTab];
};

const subMenu = {
  mobile: undefined,
  desktop: {
    type: SubMenuType.TABS,
    options: desktopTabs()
  }
};

const mainMenu = (): MainMenu => {
  return {
    menuTitle: 'storageone.productListFrontsTitle',
    behaviors: {
      productGroupEntered: [showDoors],
      productGroupExited: [forgetFrontChangeUserChoices, enableUndoRedo]
    },
    filters: enableFrontListTab ? [frontTypeFilter, colorFilter] : [colorFilter],
    widgets: widgets[platform],
    subMenu: subMenu[platform]
  };
};

export default mainMenu;
